import { useCallback, useMemo, useState, type RefObject } from 'react';

import { listContextAt, variablesInContext } from '../utils/template-validation';
import { replaceRangeWithText } from '../utils/insert-text';

export type Suggestion = {
  value: string;
  type: 'variable' | 'block' | 'closing';
};

export type UseTemplateAutocompleteOptions = {
  value: string;
  validVariables: readonly string[];
  listVariables: Record<string, readonly string[]>;
  actionVariables: readonly string[];
  onValueChange: (value: string) => void;
  /**
   * When given, an accepted suggestion is written through the browser so it
   * joins the field's undo stack. Without it the insertion still lands, via
   * onValueChange, but Ctrl+Z cannot reach past it.
   */
  textareaRef?: RefObject<HTMLTextAreaElement | null>;
};

type AutocompleteState = {
  isOpen: boolean;
  partial: string;
  exprStart: number;
  selectedIndex: number;
};

const BLOCK_HELPERS: Suggestion[] = [
  { value: '#each', type: 'block' },
  { value: '#if', type: 'block' },
  { value: '#else', type: 'block' },
  { value: '#empty', type: 'block' },
  { value: '#select', type: 'block' },
];

const CLOSING_HELPERS: Suggestion[] = [
  { value: '/each', type: 'closing' },
  { value: '/if', type: 'closing' },
  { value: '/select', type: 'closing' },
];

function findNearestOpenBlock(textBeforeCursor: string): string | null {
  const blockOpenRegex = /\{\{#(each|if|select)\s/g;
  const blockCloseRegex = /\{\{\/(each|if|select)\}\}/g;

  const opens: string[] = [];
  let m: RegExpExecArray | null;

  while ((m = blockOpenRegex.exec(textBeforeCursor)) !== null) {
    opens.push(m[1]);
  }

  while ((m = blockCloseRegex.exec(textBeforeCursor)) !== null) {
    const closeType = m[1];
    const idx = opens.lastIndexOf(closeType);
    if (idx !== -1) {
      opens.splice(idx, 1);
    }
  }

  return opens.length > 0 ? opens[opens.length - 1] : null;
}

function findUnclosedExpr(
  text: string,
  cursorPos: number,
): { partial: string; exprStart: number } | null {
  const before = text.substring(0, cursorPos);
  const lastOpen = before.lastIndexOf('{{');
  if (lastOpen === -1) return null;

  const afterOpen = before.substring(lastOpen + 2);
  if (afterOpen.includes('}}')) return null;

  const closeAfterOpen = text.indexOf('}}', lastOpen + 2);
  if (closeAfterOpen !== -1 && closeAfterOpen < cursorPos) return null;

  const nextClose = text.indexOf('}}', lastOpen + 2);
  if (nextClose !== -1 && nextClose >= cursorPos) {
    const betweenOpenAndClose = text.substring(lastOpen + 2, nextClose).trim();
    if (!betweenOpenAndClose.includes('{{')) return null;
  }

  return { partial: afterOpen, exprStart: lastOpen };
}

export function useTemplateAutocomplete(options: UseTemplateAutocompleteOptions) {
  const { value, validVariables, listVariables, actionVariables, onValueChange, textareaRef } =
    options;

  const [state, setState] = useState<AutocompleteState>({
    isOpen: false,
    partial: '',
    exprStart: -1,
    selectedIndex: 0,
  });

  // Which list's row the cursor sits on, if any. An #each or #select replaces
  // the render context with the row, so the action's own variables stop
  // resolving there and the row's take over.
  const listContext = useMemo(
    () => (state.exprStart >= 0 ? listContextAt(value, state.exprStart) : undefined),
    [value, state.exprStart],
  );

  const contextVariables = useMemo(
    (): readonly string[] =>
      variablesInContext({
        template: value,
        // -1 while closed, which would otherwise slice the last character off.
        cursor: Math.max(state.exprStart, 0),
        listVariables,
        actionVariables,
        validVariables,
      }),
    [value, state.exprStart, listVariables, actionVariables, validVariables],
  );

  const variableSuggestions = useMemo(
    (): Suggestion[] => contextVariables.map((v) => ({ value: v, type: 'variable' as const })),
    [contextVariables],
  );

  const allSuggestions = useMemo(
    () => [...variableSuggestions, ...BLOCK_HELPERS, ...CLOSING_HELPERS],
    [variableSuggestions],
  );

  // A nested loop can only iterate a list carried on the current row, so
  // offering every list would name several that cannot resolve there.
  const listVariableNames = useMemo(() => {
    if (listContext) {
      const rowVariables = listVariables[listContext];
      if (rowVariables) return rowVariables.filter((name) => listVariables[name]);
    }
    return Object.keys(listVariables);
  }, [listVariables, listContext]);

  const suggestions = useMemo((): Suggestion[] => {
    if (!state.isOpen) return [];
    const rawPartial = state.partial.toLowerCase();
    const partial = rawPartial.trim();

    const blockArgMatch = /^#(each|select)\s+(.*)$/.exec(rawPartial);
    if (blockArgMatch) {
      const argPartial = blockArgMatch[2].trim().toLowerCase();
      return listVariableNames
        .filter((name) => name.toLowerCase().startsWith(argPartial))
        .map((name) => ({ value: name, type: 'variable' as const }));
    }

    if (partial.startsWith('#')) {
      return BLOCK_HELPERS.filter((s) => s.value.toLowerCase().startsWith(partial));
    }

    if (partial.startsWith('/')) {
      const filtered = CLOSING_HELPERS.filter((s) => s.value.toLowerCase().startsWith(partial));
      const nearestBlock = findNearestOpenBlock(value.substring(0, state.exprStart));
      if (nearestBlock) {
        const preferred = `/${nearestBlock}`;
        return [...filtered].sort((a, b) => {
          if (a.value === preferred) return -1;
          if (b.value === preferred) return 1;
          return 0;
        });
      }
      return filtered;
    }

    return allSuggestions.filter((s) => s.value.toLowerCase().startsWith(partial));
  }, [state.isOpen, state.partial, state.exprStart, value, allSuggestions, listVariableNames]);

  const handleCursorChange = useCallback((textarea: HTMLTextAreaElement) => {
    const cursorPos = textarea.selectionStart;
    const result = findUnclosedExpr(textarea.value, cursorPos);

    if (result) {
      setState((prev) => ({
        isOpen: true,
        partial: result.partial,
        exprStart: result.exprStart,
        selectedIndex:
          prev.partial === result.partial && prev.exprStart === result.exprStart
            ? prev.selectedIndex
            : 0,
      }));
    } else {
      setState((prev) => ({ ...prev, isOpen: false, partial: '', exprStart: -1 }));
    }
  }, []);

  // The suggestion replaces the half-typed expression, so the range handed to
  // the browser starts at the "{{" and ends at the caret.
  const applyInsertion = useCallback(
    (insertion: string) => {
      const start = state.exprStart;
      const end = start + 2 + state.partial.length;

      const textarea = textareaRef?.current;
      if (!textarea || !replaceRangeWithText(textarea, start, end, insertion)) {
        onValueChange(value.substring(0, start) + insertion + value.substring(end));
      }

      setState((prev) => ({ ...prev, isOpen: false, partial: '', exprStart: -1 }));
    },
    [value, state.exprStart, state.partial, onValueChange, textareaRef],
  );

  const selectSuggestion = useCallback(
    (suggestionValue: string) => {
      if (state.exprStart < 0) return;

      const blockArgMatch = /^#(each|select)\s+/.exec(state.partial);
      if (blockArgMatch) {
        applyInsertion(`{{${blockArgMatch[0]}${suggestionValue}}}`);
        return;
      }

      const suggestion = allSuggestions.find((s) => s.value === suggestionValue);
      if (!suggestion) return;

      applyInsertion(
        suggestion.type === 'block' ? `{{${suggestion.value} ` : `{{${suggestion.value}}}`,
      );
    },
    [state.exprStart, state.partial, allSuggestions, applyInsertion],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!state.isOpen || suggestions.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setState((prev) => ({
          ...prev,
          selectedIndex: (prev.selectedIndex + 1) % suggestions.length,
        }));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setState((prev) => ({
          ...prev,
          selectedIndex: (prev.selectedIndex - 1 + suggestions.length) % suggestions.length,
        }));
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const selected = suggestions[state.selectedIndex];
        if (selected) {
          selectSuggestion(selected.value);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setState((prev) => ({ ...prev, isOpen: false }));
      }
    },
    [state.isOpen, state.selectedIndex, suggestions, selectSuggestion],
  );

  const dismiss = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  return {
    suggestions,
    isOpen: state.isOpen,
    selectedIndex: state.selectedIndex,
    handleCursorChange,
    handleKeyDown,
    selectSuggestion,
    dismiss,
  };
}
